import { type ComponentProps, useCallback } from "react";
import type { Task } from "@common/types/task";
import {
    type PendingStatusChange,
    useTerminatingStatusConfirmation,
} from "@hooks/useTerminatingStatusGuard";
import type { TaskStatusTerminationConfirmationModal } from "../TaskStatusTerminationConfirmationModal";
import type { TaskListUpdate } from "./useTaskListUpdate";

/**
 * Status changes for any task in the list, asking first when one would finish
 * a task that still has open blockers, as `useTerminatingStatusGuard` does
 * for a single task. Spread `confirmation` onto
 * `TaskStatusTerminationConfirmationModal`.
 */
export const useTaskListStatusChange = (update: TaskListUpdate) => {
    const applyStatus = useCallback(
        ({ task, status }: PendingStatusChange<Task>) =>
            update({ task, body: { status } }),
        [update],
    );
    const {
        changeStatus,
        pending,
        confirmChange,
        cancelChange,
        showDontAskAgain,
        shouldSkipConfirmation,
        setShouldSkipConfirmation,
    } = useTerminatingStatusConfirmation(applyStatus);

    return {
        changeStatus,
        confirmation: {
            pendingStatus: pending?.status ?? null,
            activeBlockerCount: pending?.task.numberOfActiveBlockedByTasks ?? 0,
            onConfirm: confirmChange,
            onCancel: cancelChange,
            showDontAskAgain,
            shouldSkipConfirmation,
            onShouldSkipConfirmationChange: setShouldSkipConfirmation,
        } satisfies ComponentProps<
            typeof TaskStatusTerminationConfirmationModal
        >,
    };
};
